import { create } from "zustand";
import {
  LoginResponse,
  User,
  UserDetailsResponse,
  UserState,
} from "@/types/user";
import { TwoFactorLoginState } from "@/types/twoFactor";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface EnhancedLoginResponse {
  requires_2fa: boolean;
  temp_token?: string;
  access?: string;
  refresh?: string;
  message: string;
}

interface Complete2FAResponse {
  success: boolean;
  access: string;
  refresh: string;
  message: string;
}

interface AuthStore extends UserState {
  login: (credentials: { email: string; password: string }) => Promise<void>;
  verify2FA: (code: string, useBackupCode?: boolean) => Promise<void>;
  logout: () => void;
  fetchUserDetails: () => Promise<void>;
  initialize: () => Promise<void>;
  isLoading: boolean;
  initialized: boolean;
  // 2FA specific state
  twoFactorRequired: boolean;
  tempToken: string | null;
  clearTwoFactorState: () => void;
  // Error handling
  error: string | null;
  clearError: () => void;
}

const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  initialized: false,
  twoFactorRequired: false,
  tempToken: null,
  error: null,

  initialize: async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      set({ isLoading: false, initialized: true });
      return;
    }

    try {
      const userDetails = await ApiService.request<UserDetailsResponse>(
        EndpointKeys.GET_USER_DETAILS,
      );

      // Use proper type narrowing
      let user: User;

      if (userDetails.isNanny) {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: "nanny" as const,
          employerEmployeeId: userDetails.nannyDetails?.employerEmployeeId || 0,
          isNanny: true,
          isPartner: false,
          isContractor: false,
          isClient: false,
          isSuperUser: false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      } else if (userDetails.userType === "PARTNER") {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: "PARTNER" as const,
          companyName: userDetails.partnerDetails?.companyName || "",
          isNanny: false,
          isPartner: true,
          isContractor: false,
          isClient: false,
          isSuperUser: false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      } else if (userDetails.userType === "CONTRACTOR") {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: "CONTRACTOR",
          isNanny: false,
          isPartner: false,
          isContractor: true,
          isClient: false,
          isSuperUser: false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      } else {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: userDetails.isSuperUser
            ? ("superuser" as const)
            : ("employer" as const),
          isNanny: false,
          isPartner: false,
          isContractor: false,
          isClient: userDetails.isClient || false,
          isSuperUser: userDetails.isSuperUser || false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      }

      set({ 
        user, 
        isAuthenticated: true, 
        isLoading: false, 
        initialized: true,
        error: null 
      });
    } catch (error) {
      set({ 
        isLoading: false, 
        initialized: true,
        error: null 
      });
      console.error("Failed to initialize auth:", error);
    }
  },

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      // Normalize email to lowercase for consistency
      const normalizedCredentials = {
        ...credentials,
        email: credentials.email.toLowerCase().trim(),
      };

      // Use enhanced login endpoint
      const response = await ApiService.request<EnhancedLoginResponse>(
        EndpointKeys.ENHANCED_LOGIN,
        {
          method: "POST",
          body: normalizedCredentials,
          skipAuth: true,
        },
      );

      if (response.requires_2fa) {
        // 2FA is required, store temp token and set state
        set({
          twoFactorRequired: true,
          tempToken: response.temp_token || null,
          isLoading: false,
          error: null,
        });
        return;
      } else {
        // No 2FA required, proceed with normal login
        const accessToken = response.access;
        const refreshToken = response.refresh;

        if (!accessToken || !refreshToken) {
          throw new Error('Invalid response: missing tokens');
        }

        // Store tokens
        localStorage.setItem("authToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        // Set the token in API service before making authenticated requests
        ApiService.setAuthToken(accessToken);

        // Fetch user details
        const userDetails = await ApiService.request<UserDetailsResponse>(
          EndpointKeys.GET_USER_DETAILS,
        );

        // Convert API response to User type
        let user: User;
        if (userDetails.isNanny) {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: "nanny",
            employerEmployeeId: userDetails.nannyDetails?.employerEmployeeId || 0,
            isNanny: true,
            isPartner: false,
            isContractor: false,
            isClient: false,
            isSuperUser: false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        } else if (userDetails.userType === "PARTNER") {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: "PARTNER",
            companyName: userDetails.partnerDetails?.companyName || "",
            isNanny: false,
            isPartner: true,
            isContractor: false,
            isClient: false,
            isSuperUser: false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        } else if (userDetails.userType === "CONTRACTOR") {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: "CONTRACTOR",
            isNanny: false,
            isPartner: false,
            isContractor: true,
            isClient: false,
            isSuperUser: false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        } else {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: userDetails.isSuperUser ? "superuser" : "employer",
            isNanny: false,
            isPartner: false,
            isContractor: false,
            isClient: userDetails.isClient || false,
            isSuperUser: userDetails.isSuperUser || false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        }

        // Complete login
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          initialized: true,
          twoFactorRequired: false,
          tempToken: null,
          error: null,
        });
      }
    } catch (error) {
      // On error, reset everything
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        initialized: true,
        twoFactorRequired: false,
        tempToken: null,
        error: error instanceof Error ? error.message : "Login failed",
      });
      console.error("Login failed:", error);
      throw error;
    }
  },

  verify2FA: async (code: string, useBackupCode = false) => {
    const { tempToken } = get();
    if (!tempToken) {
      throw new Error("No temporary token available");
    }

    set({ isLoading: true, error: null });
    try {
      const response = await ApiService.request<Complete2FAResponse>(
        EndpointKeys.COMPLETE_2FA_LOGIN,
        {
          method: "POST",
          body: {
            temp_token: tempToken,
            code,
            use_backup_code: useBackupCode,
          },
          skipAuth: true,
        },
      );

      if (response.success) {
        // Store tokens
        localStorage.setItem("authToken", response.access);
        localStorage.setItem("refreshToken", response.refresh);

        // Set the token in API service
        ApiService.setAuthToken(response.access);

        // Fetch user details
        const userDetails = await ApiService.request<UserDetailsResponse>(
          EndpointKeys.GET_USER_DETAILS,
        );

        // Convert API response to User type
        let user: User;
        if (userDetails.isNanny) {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: "nanny",
            employerEmployeeId: userDetails.nannyDetails?.employerEmployeeId || 0,
            isNanny: true,
            isPartner: false,
            isContractor: false,
            isClient: false,
            isSuperUser: false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        } else if (userDetails.userType === "PARTNER") {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: "PARTNER",
            companyName: userDetails.partnerDetails?.companyName || "",
            isNanny: false,
            isPartner: true,
            isContractor: false,
            isClient: false,
            isSuperUser: false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        } else if (userDetails.userType === "CONTRACTOR") {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: "CONTRACTOR",
            isNanny: false,
            isPartner: false,
            isContractor: true,
            isClient: false,
            isSuperUser: false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        } else {
          user = {
            id: userDetails.id,
            email: userDetails.email,
            userType: userDetails.isSuperUser ? "superuser" : "employer",
            isNanny: false,
            isPartner: false,
            isContractor: false,
            isClient: userDetails.isClient || false,
            isSuperUser: userDetails.isSuperUser || false,
            inImplementation: userDetails.inImplementation,
            demo_account: userDetails.demo_account,
            hasActiveSubscription: userDetails.hasActiveSubscription,
            subscriptionStatus: userDetails.subscriptionStatus,
          };
        }

        // Complete 2FA login
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          initialized: true,
          twoFactorRequired: false,
          tempToken: null,
          error: null,
        });
      } else {
        throw new Error(response.message || "2FA verification failed");
      }
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : "2FA verification failed",
      });
      console.error("2FA verification failed:", error);
      throw error;
    }
  },

  fetchUserDetails: async () => {
    try {
      const userDetails = await ApiService.request<UserDetailsResponse>(
        EndpointKeys.GET_USER_DETAILS,
      );

      let user: User;

      if (userDetails.isNanny) {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: "nanny",
          employerEmployeeId: userDetails.nannyDetails?.employerEmployeeId || 0,
          isNanny: true,
          isPartner: false,
          isContractor: false,
          isClient: false,
          isSuperUser: false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      } else if (userDetails.userType === "PARTNER") {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: "PARTNER",
          companyName: userDetails.partnerDetails?.companyName || "",
          isNanny: false,
          isPartner: true,
          isContractor: false,
          isClient: false,
          isSuperUser: false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      } else if (userDetails.userType === "CONTRACTOR") {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: "CONTRACTOR",
          isNanny: false,
          isPartner: false,
          isContractor: true,
          isClient: false,
          isSuperUser: false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      } else {
        user = {
          id: userDetails.id,
          email: userDetails.email,
          userType: userDetails.isSuperUser ? "superuser" : "employer",
          isNanny: false,
          isPartner: false,
          isContractor: false,
          isClient: userDetails.isClient || false,
          isSuperUser: userDetails.isSuperUser || false,
          inImplementation: userDetails.inImplementation,
          demo_account: userDetails.demo_account,
          hasActiveSubscription: userDetails.hasActiveSubscription,
          subscriptionStatus: userDetails.subscriptionStatus,
        };
      }

      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      console.error("fetchUserDetails error:", error);
      set({ isLoading: false });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    set({ 
      user: null, 
      isAuthenticated: false, 
      isLoading: false,
      twoFactorRequired: false,
      tempToken: null,
      error: null,
    });
  },

  clearTwoFactorState: () => {
    set({
      twoFactorRequired: false,
      tempToken: null,
      error: null,
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));

export const useAuth = () => {
  const store = useAuthStore();
  return store;
};